import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import {
  applySearchResultsVisibility,
  countVisibleCards,
  parseVisibilityPayloadFromSectionHTML,
} from '@theme/search-results-visibility';

/** Product cards a shopper can see: excludes visibility-hidden, pending, batch-deferred and sponsored cards. */
const DISPLAYED_RESULT_SELECTOR =
  'li[data-product-id][ref="cards[]"]' +
  ':not([data-isbn-visibility-hidden="true"])' +
  ':not([data-pdp-visibility-pending="true"])' +
  ':not([data-bz-deferred])' +
  ':not([data-inserted-by="koddi"])';

export const RESULTS_READY_EVENT = 'follett:results-ready';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {boolean} */
  #loadingMore = false;

  /** Last page fetched (or attempted). Used so empty pages do not block load more. */
  #lastFetchedPage = 1;

  /** True while a ?page=N URL is being reset to page 1; readiness is reported once it finishes. */
  #resetPending = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.dataset.loadMore === 'true') {
      this.#lastFetchedPage = this.#getHighestPageFromDom();
      this.#normalizeSearchUrlOnLoad();
      this.#setupLoadMoreButton();
      this.#updateLoadMoreButton();
      document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdateLoadMore);
      document.addEventListener('isbn-visibility-complete', this.#handleVisibilityComplete, {
        once: true,
      });
    }

    // Grids with a visibility payload are final only after search-results-isbn-visibility
    // completes; data-visibility-complete covers completion before this element was defined.
    if (!this.querySelector('#isbn-search-visibility-payload')) {
      this.markResultsReady('initial-load');
    } else if (this.dataset.visibilityComplete === 'true') {
      this.#handleVisibilityComplete();
    }
  }

  /**
   * Reports the displayed result count once the grid is final: stamped on the element as
   * data-results-* for listeners that attach later, and dispatched as follett:results-ready.
   * @param {string} source - What produced this state, e.g. 'initial-load'
   */
  markResultsReady(source) {
    if (this.#resetPending) return;

    const count = this.querySelectorAll(DISPLAYED_RESULT_SELECTOR).length;
    const hasMore =
      this.dataset.loadMore === 'true'
        ? this.#hasMorePages()
        : Boolean(this.querySelector('.main-collection-pagination')?.children.length);

    this.dataset.resultsCount = String(count);
    this.dataset.resultsHasMore = String(hasMore);
    this.dataset.resultsReady = 'true';
    this.dispatchEvent(
      new CustomEvent(RESULTS_READY_EVENT, { bubbles: true, detail: { count, hasMore, source } })
    );
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  resolvePage(type) {
    if (this.dataset.loadMore !== 'true' || type !== 'next') return undefined;

    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage || 1);
    const nextPage = this.#lastFetchedPage + 1;

    if (nextPage > lastPage) return undefined;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(nextPage));
    url.hash = '';

    return { page: nextPage, url };
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdateLoadMore);
    document.removeEventListener('isbn-visibility-complete', this.#handleVisibilityComplete);
  }

  #handleVisibilityComplete = () => {
    this.#updateLoadMoreButton();
    if (this.dataset.resultsReady === 'true') return;
    this.markResultsReady('initial-load');
  };

  async loadNextPage() {
    const pageNumber = this.#getUpcomingPageNumber();
    if (!pageNumber) return false;

    const { grid } = this.refs;
    const countBefore = grid?.querySelectorAll(':scope > [ref="cards[]"]').length ?? 0;

    await super.loadNextPage();

    // Always advance so empty/filtered pages cannot stall pagination on the same page number.
    this.#lastFetchedPage = pageNumber;

    const countAfter = grid?.querySelectorAll(':scope > [ref="cards[]"]').length ?? 0;
    const cardsAppended = countAfter > countBefore;

    if (cardsAppended) {
      await this.#applyVisibilityForNewlyLoadedPage(pageNumber);
      this.#keepUncheckedAdoptedTitleCardsHidden();
    }

    this.#updateLoadMoreButton();

    return cardsAppended;
  }

  /**
   * adoptedtitle cards without a completed check stay hidden (fail-safe).
   */
  #keepUncheckedAdoptedTitleCardsHidden() {
    this.querySelectorAll(
      '[data-requires-adoption-check="true"]:not([data-pdp-visibility-checked])'
    ).forEach((card) => {
      card.setAttribute('data-pdp-visibility-pending', 'true');
    });
  }

  #handleFilterUpdateLoadMore = () => {
    requestIdleCallback(() => {
      this.#lastFetchedPage = this.#getHighestPageFromDom();
      this.#updateLoadMoreButton();
    });
  };

  #setupLoadMoreButton() {
    const { loadMoreButton } = this.refs;
    if (!(loadMoreButton instanceof HTMLButtonElement)) return;

    loadMoreButton.addEventListener('click', this.#handleLoadMoreClick);
  }

  #handleLoadMoreClick = async () => {
    if (this.#loadingMore || !this.#hasMorePages()) return;

    this.#loadingMore = true;
    this.classList.add('isbn-search-load-more-pending');
    this.#updateLoadMoreButton();

    try {
      await this.#loadMoreUntilMinimumBatch();
    } finally {
      this.#loadingMore = false;
      this.classList.remove('isbn-search-load-more-pending');
      this.#updateLoadMoreButton();
    }
  };

  async #loadMoreUntilMinimumBatch() {
    const minBatch = Number(this.dataset.minLoadMoreBatch || 5);
    const maxPagesPerClick = Number(this.dataset.maxPagesPerLoadMore || 15);
    let addedVisible = 0;
    let pagesAttempted = 0;

    while (addedVisible < minBatch && this.#hasMorePages() && pagesAttempted < maxPagesPerClick) {
      pagesAttempted += 1;
      const visibleBefore = this.#countVisibleCards();

      await this.loadNextPage();

      const visibleAfter = this.#countVisibleCards();
      addedVisible += Math.max(0, visibleAfter - visibleBefore);

      if (!this.#hasMorePages()) break;
    }
  }

  async #normalizeSearchUrlOnLoad() {
    const url = new URL(window.location.href);
    const pageParam = url.searchParams.get('page');

    if (!pageParam) return;

    url.searchParams.delete('page');
    history.replaceState('', '', url.toString());

    if (Number(pageParam) > 1) {
      this.#resetPending = true;
      try {
        await this.#resetToFirstPage(url);
      } finally {
        this.#resetPending = false;
        this.markResultsReady('url-reset-page-1');
      }
    }
  }

  /**
   * @param {URL} pageOneUrl - Current location without a page param
   */
  async #resetToFirstPage(pageOneUrl) {
    const { grid } = this.refs;
    if (!grid) return;

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, pageOneUrl);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[ref="grid"]');

      if (!newGrid) return;

      grid.replaceChildren(...newGrid.children);

      if (newGrid.dataset.lastPage) {
        grid.dataset.lastPage = newGrid.dataset.lastPage;
      }

      if (newGrid.dataset.pageSize) {
        grid.dataset.pageSize = newGrid.dataset.pageSize;
      }

      this.pages.clear();
      this.#lastFetchedPage = 1;

      const payloads = parseVisibilityPayloadFromSectionHTML(html);
      if (payloads?.length) {
        await applySearchResultsVisibility(this, payloads, { source: 'url-reset-page-1' });
      }

      this.#updateLoadMoreButton();
    } catch (error) {
      console.error('Unable to reset search results to page 1', error);
    }
  }

  #isPaginationExhausted() {
    const { grid } = this.refs;
    if (!grid) return true;

    const lastPage = Number(grid.dataset.lastPage || 1);
    return this.#lastFetchedPage >= lastPage;
  }

  #updateLoadMoreButton() {
    const { loadMoreButton, loadMoreWrapper } = this.refs;

    if (!(loadMoreWrapper instanceof HTMLElement)) return;

    const exhausted = this.#isPaginationExhausted();
    const canLoadMore = !exhausted && !this.#loadingMore;

    loadMoreWrapper.classList.toggle('main-collection-load-more--exhausted', exhausted);
    loadMoreWrapper.hidden = false;

    if (loadMoreButton instanceof HTMLButtonElement) {
      loadMoreButton.disabled = !canLoadMore;
      loadMoreButton.setAttribute('aria-disabled', canLoadMore ? 'false' : 'true');
      loadMoreButton.setAttribute('aria-busy', this.#loadingMore ? 'true' : 'false');
    }
  }

  #hasMorePages() {
    return !this.#isPaginationExhausted();
  }

  #countVisibleCards() {
    return countVisibleCards(this);
  }

  #getUpcomingPageNumber() {
    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage || 1);
    const nextPage = this.#lastFetchedPage + 1;

    if (nextPage > lastPage) return undefined;

    return nextPage;
  }

  #getHighestPageFromDom() {
    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) return 1;

    return cards.reduce((max, card) => {
      const page = Number(card.dataset.page) || 1;
      return page > max ? page : max;
    }, 1);
  }

  async #applyVisibilityForNewlyLoadedPage(pageNumber) {
    const pageHTML = pageNumber ? this.getPageHTML(pageNumber) : undefined;
    const payloads = pageHTML ? parseVisibilityPayloadFromSectionHTML(pageHTML) : null;

    if (!payloads?.length) {
      return;
    }

    await applySearchResultsVisibility(this, payloads, {
      source: pageNumber ? `load-more-page-${pageNumber}` : 'load-more',
    });
  }

  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
